import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import './schedule-pages.css';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduleApi } from '../api/endpoints.ts';
import { Alert, Button, LoadingBlock, PageHeader } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import AddressLink from '../components/address-link.tsx';
import { formatMinutes } from '../i18n/format.ts';
import { useLocalizedContent } from '../i18n/localized.ts';
import { VisitChecklist } from '../components/schedule/visit-checklist.tsx';
import { VisitInstructionsCard } from '../components/schedule/visit-instructions.tsx';
import { formatLocalTime, formatLongDate } from '../i18n/datetime.ts';
import type { LocalizedText } from '@shared/constants/localized-text.ts';

interface WorksheetSectionRoom {
  groupId?: string | null;
  groupName: string | null;
  groupNameTranslations: LocalizedText | null;
  groupQuantity: number;
}

/**
 * Rooms that share a group stay under one heading. The group's id is what
 * makes them the same group: two "Apartment ×40" stacks are two headings.
 * An unnamed group of one is the residential default, and it draws no heading.
 */
export function worksheetSections<T extends WorksheetSectionRoom>(rooms: readonly T[]) {
  const sections: Array<{
    key: string;
    heading: { name: string | null; translations: LocalizedText | null; quantity: number } | null;
    rooms: T[];
  }> = [];
  for (const [index, room] of rooms.entries()) {
    const quantity = room.groupQuantity > 0 ? room.groupQuantity : 1;
    const name = room.groupName && room.groupName.trim().length > 0 ? room.groupName : null;
    const heading = name || quantity > 1 ? { name, translations: room.groupNameTranslations, quantity } : null;
    const signature = `${room.groupId ?? ''}\u0000${heading ? `${heading.name ?? ''}\u0000${heading.quantity}` : ''}`;
    const previous = sections[sections.length - 1];
    if (previous && previous.key.endsWith(`:${signature}`) && previous.heading?.name === heading?.name) {
      previous.rooms.push(room);
      continue;
    }
    sections.push({ key: `${index}:${signature}`, heading, rooms: [room] });
  }
  return sections;
}

/**
 * The printable work order for one visit: when and where, how to get in, and
 * the room-by-room task list that was sold — straight from the quote the
 * schedule was created from, so the person cleaning works the scope the
 * customer agreed to. The print button relies on the app chrome being
 * `print:hidden`, so what comes out of the printer is just this sheet.
 */
/** The page exists only on a plan that carries the calendar (plan-gate.tsx). */
export default function WorkOrderPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  if (!usePlanFeature('scheduling')) return <PlanRequiredPage feature="scheduling" title={t('schedule.page.title')} />;
  return <WorkOrderPageContent embedded={embedded} />;
}

function WorkOrderPageContent({ embedded }: { embedded: boolean }) {
  const { t } = useTranslation();
  /*
    The checklist in the language of whoever is holding this sheet — which is
    the whole point of the room and task translations (see localized-text.ts). The quote it came
    from is written in the customer's language; the person cleaning is not the
    customer, and on a bilingual crew two people open this page and each gets
    their own.
  */
  const localized = useLocalizedContent();
  useDocumentTitle(t('workOrder.documentTitle'));
  const { appointmentId = '' } = useParams();

  const appointment = useQuery({
    queryKey: ['appointment', appointmentId],
    queryFn: () => scheduleApi.appointment(appointmentId),
    enabled: appointmentId.length > 0,
  });
  const worksheet = useQuery({
    queryKey: ['worksheet', appointmentId],
    queryFn: () => scheduleApi.worksheet(appointmentId),
    enabled: appointmentId.length > 0,
  });

  if (appointment.isLoading || worksheet.isLoading) {
    const loading = <LoadingBlock label={t('workOrder.loading')} />;
    return embedded ? loading : <WorkPage title={t('workOrder.title')}>{loading}</WorkPage>;
  }
  if (appointment.isError || worksheet.isError) {
    const failure = (
      <Alert tone="error" title={t('workOrder.loadErrorTitle')}>
        <p>{((appointment.error ?? worksheet.error) as Error).message}</p>
        <Button
          className="mt-3"
          busy={appointment.isFetching || worksheet.isFetching}
          onClick={() => {
            void appointment.refetch();
            void worksheet.refetch();
          }}
        >
          {t('common.retry')}
        </Button>
      </Alert>
    );
    return embedded ? (
      failure
    ) : (
      <WorkPage
        title={t('workOrder.title')}
        actions={
          <Link to="/schedule" className="btn btn-secondary">
            {t('workOrder.backToSchedule')}
          </Link>
        }
      >
        {failure}
      </WorkPage>
    );
  }
  if (!appointment.data || !worksheet.data) return null;

  const visit = appointment.data;
  const sheet = worksheet.data;
  const siteContact = [sheet.property?.siteContactName, sheet.property?.siteContactTitle].filter(Boolean).join(' · ');
  const propertyNotes = [
    // First in the list on purpose: the crew's first problem at a commercial
    // building is not the alarm code, it is finding the person who has it.
    siteContact
      ? {
          label: t('workOrder.siteContact'),
          text: sheet.property?.siteContactPhone ? `${siteContact} · ${sheet.property.siteContactPhone}` : siteContact,
        }
      : null,
    sheet.property?.accessNotes ? { label: t('workOrder.accessNotes'), text: sheet.property.accessNotes } : null,
    sheet.property?.parkingNotes ? { label: t('workOrder.parkingNotes'), text: sheet.property.parkingNotes } : null,
    sheet.property?.petsDescription ? { label: t('workOrder.pets'), text: sheet.property.petsDescription } : null,
  ].filter((note): note is { label: string; text: string } => note !== null);

  return (
    <div className="work-page schedule-work-order">
      {!embedded && (
        <div className="print:hidden">
          <PageHeader
            title={t('workOrder.title')}
            actions={
              <>
                <Link to="/schedule" className="btn btn-secondary">
                  {t('workOrder.backToSchedule')}
                </Link>
                <Button variant="primary" onClick={() => window.print()}>
                  {t('workOrder.print')}
                </Button>
              </>
            }
          />
        </div>
      )}

      <article className="card max-w-3xl">
        {!embedded && (
          <header className="mb-4 border-b border-[color:var(--color-line)] pb-4">
            <h1 className="text-xl font-bold">{visit.customerName}</h1>
            <p className="text-lg">
              {formatLongDate(visit.scheduledDate)} · {formatLocalTime(visit.startTimeLocal)} ·{' '}
              {formatMinutes(visit.durationMinutes)}
            </p>
            {visit.propertyAddress ? (
              <p className="text-[color:var(--color-muted)]">
                <AddressLink address={visit.propertyAddress} />
              </p>
            ) : null}
            {sheet.property?.siteCode ? (
              <p className="text-[color:var(--color-muted)]">
                {t('workOrder.siteCode', { code: sheet.property.siteCode })}
              </p>
            ) : null}
            {sheet.quoteNumber ? (
              <p className="mt-1 text-sm text-[color:var(--color-muted)]">
                {t('workOrder.fromQuote', { number: sheet.quoteNumber })}
              </p>
            ) : null}
          </header>
        )}

        <VisitInstructionsCard
          key={visit.id}
          appointmentId={visit.id}
          closed={['completed', 'cancelled', 'no_show'].includes(visit.status)}
        />
        {sheet.briefing ? (
          <section className="mb-4">
            <h2 className="mb-2 text-base font-semibold">{t('workOrder.briefing')}</h2>
            <p className="text-sm">{sheet.briefing}</p>
          </section>
        ) : null}

        {propertyNotes.length > 0 ? (
          <section className="mb-4">
            <h2 className="mb-2 text-base font-semibold">{t('visitInstructions.everyVisit')}</h2>
            <dl className="space-y-1 text-sm">
              {propertyNotes.map((note) => (
                <div key={note.label}>
                  <dt className="font-medium">{note.label}</dt>
                  <dd className="text-[color:var(--color-muted)]">{note.text}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {visit.internalNotes ? (
          <section className="mb-4">
            <h2 className="mb-1 text-base font-semibold">{t('workOrder.visitNotes')}</h2>
            <p className="text-sm text-[color:var(--color-muted)]">{visit.internalNotes}</p>
          </section>
        ) : null}

        {(sheet.checklist?.items.length ?? 0) > 0 ? (
          <VisitChecklist
            appointmentId={appointmentId}
            mode={embedded ? 'panel' : 'sheet'}
            initialData={sheet.checklist ?? undefined}
          />
        ) : null}

        {sheet.rooms.length === 0 && (sheet.checklist?.items.length ?? 0) === 0 ? (
          <WorkEmpty title={t('workOrder.noChecklist')} />
        ) : sheet.rooms.length > 0 ? (
          worksheetSections(sheet.rooms).map((section) => (
            <section key={section.key} className="mb-4">
              {section.heading ? (
                <h2 className="mb-2 text-base font-semibold">
                  {section.heading.name
                    ? localized.text(section.heading.name, section.heading.translations)
                    : t('roomsStep.ungroupedName')}
                  {section.heading.quantity > 1 ? ` ×${section.heading.quantity}` : ''}
                </h2>
              ) : null}
              {section.rooms.map((room, index) => (
                <div key={`${room.roomName}-${index}`} className="mb-4">
                  <h3 className="mb-1 text-base font-semibold">
                    {localized.text(room.roomName, room.roomNameTranslations)}
                    {room.quantity > 1 ? ` ×${room.quantity}` : ''}
                  </h3>
                  {room.customerNotes ? <p className="mb-1 text-sm">{room.customerNotes}</p> : null}
                  {room.internalNotes ? (
                    <p className="mb-1 text-sm text-[color:var(--color-muted)]">{room.internalNotes}</p>
                  ) : null}
                  <ul className="space-y-1 text-sm">
                    {room.tasks.map((task, taskIndex) => (
                      <li key={`${task.name}-${taskIndex}`} className="flex items-baseline gap-2">
                        {/* A real printable checkbox, not an input: this list is
                        ticked with a pen on site. */}
                        <span
                          aria-hidden="true"
                          className="inline-block h-4 w-4 shrink-0 translate-y-0.5 rounded border border-[color:var(--color-line)]"
                        />
                        <span>
                          {localized.text(task.name, task.nameTranslations)}
                          <span className="text-[color:var(--color-muted)]">
                            {' '}
                            · {formatMinutes(task.estimatedMinutes)}
                          </span>
                          {/* The spec's cadence — "carpets weekly" — so the crew
                          knows whether today is the day. Every-visit work says
                          nothing: it is the default and the tag would be noise. */}
                          {task.frequency && task.frequency !== 'every_visit' ? (
                            <span className="ml-2 badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                              {t(`servicesStep.frequency.${task.frequency}`)}
                            </span>
                          ) : null}
                          {task.customerNotes ? (
                            <span className="block text-[color:var(--color-muted)]">{task.customerNotes}</span>
                          ) : null}
                          {task.internalNotes ? (
                            <span className="block text-[color:var(--color-muted)]">{task.internalNotes}</span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))
        ) : null}
      </article>
    </div>
  );
}
